import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { modelProviderApi, type ModelProviderConfig, type LlmProvider, type ModelUseCase } from '@/lib/api';
import {
  Plus, Trash2, Check, X, Edit2, ToggleLeft, ToggleRight, RefreshCw,
  ShieldCheck, AlertTriangle, Loader2, Zap,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PageGuide } from '@/components/PageGuide';
import { SettingsEmptyState } from '@/components/SettingsEmptyState';
import { useNotification } from '@/contexts/NotificationContext';
import styles from './_auth.settings.llm-providers.module.css';

export const Route = createFileRoute('/_auth/settings/llm-providers')({
  component: LlmProvidersPage,
});

// ── Provider metadata with SVG icons ──────────────────────────────────────

const PROVIDER_META: Record<LlmProvider, {
  name: string;
  color: string;
  icon: React.ReactNode;
  keyPlaceholder: string;
  keyPrefix: string;
  docsUrl: string;
  models: { name: string; label: string; capabilities: string[] }[];
}> = {
  GOOGLE_GEMINI: {
    name: 'Google Gemini',
    color: '#4285f4',
    icon: (
      <svg width="18" height="18" viewBox="0 0 28 28" fill="none">
        <path d="M14 28C14 21.75 8.75 17 2.5 14C8.75 11 14 6.25 14 0C14 6.25 19.25 11 25.5 14C19.25 17 14 21.75 14 28Z" fill="url(#gemini_g)"/>
        <defs><linearGradient id="gemini_g" x1="2.5" y1="0" x2="25.5" y2="28" gradientUnits="userSpaceOnUse"><stop stopColor="#4285F4"/><stop offset="0.5" stopColor="#9B72CB"/><stop offset="1" stopColor="#D96570"/></linearGradient></defs>
      </svg>
    ),
    keyPlaceholder: 'AIzaSy...',
    keyPrefix: 'AIza',
    docsUrl: 'https://aistudio.google.com/app/apikey',
    models: [
      { name: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', capabilities: ['Conversation', 'Routing', 'Summarization'] },
      { name: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', capabilities: ['Conversation', 'Routing', 'Summarization'] },
      { name: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', capabilities: ['Conversation', 'Summarization'] },
      { name: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash', capabilities: ['Conversation', 'Routing', 'Summarization'] },
      { name: 'gemini-2.0-flash-lite', label: 'Gemini 2.0 Flash Lite', capabilities: ['Routing'] },
      { name: 'text-embedding-004', label: 'Text Embedding 004', capabilities: ['Embedding'] },
    ],
  },
  OPENAI: {
    name: 'OpenAI',
    color: '#10a37f',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.28 9.37a5.88 5.88 0 00-.51-4.85 5.96 5.96 0 00-6.4-2.87A5.88 5.88 0 0010.92.2a5.96 5.96 0 00-5.69 4.13A5.88 5.88 0 001.3 7.26a5.96 5.96 0 00.73 7.01 5.88 5.88 0 00.51 4.85 5.96 5.96 0 006.4 2.87A5.88 5.88 0 0013.08 23.8a5.96 5.96 0 005.69-4.13 5.88 5.88 0 003.93-2.93 5.96 5.96 0 00-.73-7.01zm-8.9 12.89a4.47 4.47 0 01-2.87-1.04l.14-.08 4.76-2.75a.77.77 0 00.39-.67v-6.72l2.01 1.16a.07.07 0 01.04.06v5.56a4.49 4.49 0 01-4.47 4.48zm-9.64-4.12a4.47 4.47 0 01-.53-3.01l.14.08 4.76 2.75a.78.78 0 00.78 0l5.81-3.36v2.32a.08.08 0 01-.03.06l-4.81 2.78a4.49 4.49 0 01-6.12-1.62zm-1.25-10.4a4.47 4.47 0 012.34-1.97v5.66a.77.77 0 00.39.67l5.81 3.35-2.01 1.16a.07.07 0 01-.07 0l-4.81-2.78a4.49 4.49 0 01-1.65-6.09zm16.57 3.86l-5.81-3.35 2.01-1.16a.07.07 0 01.07 0l4.81 2.78a4.49 4.49 0 01-.69 8.09v-5.69a.77.77 0 00-.39-.67zm2-3.02l-.14-.08-4.76-2.75a.78.78 0 00-.78 0l-5.81 3.35V7.78a.08.08 0 01.03-.06l4.81-2.78a4.49 4.49 0 016.65 4.64zm-12.58 4.15l-2.01-1.16a.07.07 0 01-.04-.06V6.15a4.49 4.49 0 017.34-3.46l-.14.08-4.76 2.75a.77.77 0 00-.39.67zm1.09-2.36l2.59-1.5 2.59 1.5v2.99l-2.59 1.5-2.59-1.5z"/>
      </svg>
    ),
    keyPlaceholder: 'sk-proj-...',
    keyPrefix: 'sk-',
    docsUrl: 'https://platform.openai.com/api-keys',
    models: [
      { name: 'gpt-4.1', label: 'GPT-4.1', capabilities: ['Conversation', 'Summarization'] },
      { name: 'gpt-4.1-mini', label: 'GPT-4.1 Mini', capabilities: ['Conversation', 'Routing', 'Summarization'] },
      { name: 'gpt-4.1-nano', label: 'GPT-4.1 Nano', capabilities: ['Routing'] },
      { name: 'gpt-4o', label: 'GPT-4o', capabilities: ['Conversation', 'Summarization'] },
      { name: 'gpt-4o-mini', label: 'GPT-4o Mini', capabilities: ['Conversation', 'Routing', 'Summarization'] },
      { name: 'o4-mini', label: 'o4-mini', capabilities: ['Conversation', 'Summarization'] },
      { name: 'o3', label: 'o3', capabilities: ['Conversation'] },
      { name: 'o3-mini', label: 'o3-mini', capabilities: ['Conversation', 'Summarization'] },
      { name: 'text-embedding-3-small', label: 'Embedding 3 Small', capabilities: ['Embedding'] },
      { name: 'text-embedding-3-large', label: 'Embedding 3 Large', capabilities: ['Embedding'] },
    ],
  },
  ANTHROPIC: {
    name: 'Anthropic',
    color: '#d97706',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.304 3.541h-3.48l6.157 16.918h3.48L17.303 3.541zm-10.65 0L.498 20.459h3.548l1.272-3.494h6.512l1.272 3.494h3.548L10.493 3.541H6.654zm-.372 10.576l2.147-5.898 2.147 5.898H6.283z"/>
      </svg>
    ),
    keyPlaceholder: 'sk-ant-api03-...',
    keyPrefix: 'sk-ant',
    docsUrl: 'https://console.anthropic.com/settings/keys',
    models: [
      { name: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4', capabilities: ['Conversation', 'Summarization'] },
      { name: 'claude-3-7-sonnet-20250219', label: 'Claude 3.7 Sonnet', capabilities: ['Conversation', 'Summarization'] },
      { name: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku', capabilities: ['Conversation', 'Routing', 'Summarization'] },
      { name: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet', capabilities: ['Conversation', 'Summarization'] },
      { name: 'claude-3-opus-20240229', label: 'Claude 3 Opus', capabilities: ['Conversation'] },
      { name: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku', capabilities: ['Routing'] },
    ],
  },
};

const USE_CASE_LABELS: Record<ModelUseCase, string> = {
  CONVERSATION:   'Conversation',
  ROUTING:        'Routing',
  SUMMARIZATION:  'Summarization',
  EMBEDDING:      'Embedding',
};

const PROVIDERS: LlmProvider[] = ['GOOGLE_GEMINI', 'OPENAI', 'ANTHROPIC'];
const USE_CASES: ModelUseCase[] = ['CONVERSATION', 'ROUTING', 'SUMMARIZATION', 'EMBEDDING'];

interface FormState {
  provider: LlmProvider;
  modelName: string;
  useCases: ModelUseCase[];
  apiKey: string;
  priority: number;
}

export default function LlmProvidersPage() {
  const { success, error: notifyError } = useNotification();
  const [configs, setConfigs] = useState<ModelProviderConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<ModelProviderConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ModelProviderConfig | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{ valid: boolean; latencyMs: number; error?: string } | null>(null);
  const [form, setForm] = useState<FormState>({
    provider: 'GOOGLE_GEMINI',
    modelName: PROVIDER_META.GOOGLE_GEMINI.models[0].name,
    useCases: ['CONVERSATION'],
    apiKey: '',
    priority: 10,
  });

  const load = () => {
    setLoading(true);
    modelProviderApi.list().then(setConfigs).finally(() => setLoading(false));
  };
  useEffect(load, []);

  function openCreate() {
    setEditTarget(null);
    setVerifyResult(null);
    const defaultModel = PROVIDER_META.GOOGLE_GEMINI.models[0].name;
    setForm({ provider: 'GOOGLE_GEMINI', modelName: defaultModel, useCases: ['CONVERSATION'], apiKey: '', priority: 10 });
    setShowModal(true);
  }

  function openEdit(config: ModelProviderConfig) {
    setEditTarget(config);
    setVerifyResult(null);
    setForm({
      provider: config.provider,
      modelName: config.modelName,
      useCases: [config.useCase],
      apiKey: '', // never pre-fill key
      priority: config.priority,
    });
    setShowModal(true);
  }

  async function handleVerify() {
    if (!form.apiKey.trim()) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const result = await modelProviderApi.verifyKey({
        provider: form.provider,
        apiKey: form.apiKey,
        modelName: form.modelName,
      });
      setVerifyResult(result);
      if (result.valid) {
        success('API key verified', `${PROVIDER_META[form.provider].name} responded in ${result.latencyMs}ms`);
      } else {
        notifyError('Key verification failed', result.error ?? 'Unknown error');
      }
    } catch (e) {
      setVerifyResult({ valid: false, latencyMs: 0, error: (e as Error).message });
      notifyError('Verification error', (e as Error).message);
    } finally {
      setVerifying(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (editTarget) {
        // Editing: update single config
        await modelProviderApi.upsert({
          id: editTarget.id,
          provider: form.provider,
          modelName: form.modelName,
          useCase: form.useCases[0],
          priority: form.priority,
          enabled: true,
          apiKey: form.apiKey || undefined,
        });
      } else {
        // Creating: one config per selected use case
        await Promise.all(form.useCases.map(uc =>
          modelProviderApi.upsert({
            provider: form.provider,
            modelName: form.modelName,
            useCase: uc,
            priority: form.priority,
            enabled: true,
            apiKey: form.apiKey || undefined,
          })
        ));
      }
      const ucLabels = form.useCases.map(u => USE_CASE_LABELS[u]).join(', ');
      success(editTarget ? 'Provider updated' : 'Provider added', `${PROVIDER_META[form.provider].name} / ${form.modelName} — ${ucLabels}`);
      setShowModal(false);
      load();
    } catch (e) {
      notifyError('Failed to save', (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(config: ModelProviderConfig) {
    await modelProviderApi.upsert({
      id: config.id,
      provider: config.provider,
      modelName: config.modelName,
      useCase: config.useCase,
      enabled: !config.enabled,
    });
    success(config.enabled ? 'Provider disabled' : 'Provider enabled');
    load();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await modelProviderApi.delete(deleteTarget.id);
      success('Provider removed');
      setDeleteTarget(null);
      load();
    } catch (e) {
      notifyError('Failed to remove', (e as Error).message);
    }
  }

  const tenantConfigs = configs.filter(c => c.tenantId !== null);

  const provMeta = PROVIDER_META[form.provider];

  // Auto-select first matching model when provider changes
  function handleProviderChange(p: LlmProvider) {
    const meta = PROVIDER_META[p];
    setForm(f => ({ ...f, provider: p, modelName: meta.models[0].name }));
    setVerifyResult(null);
  }

  function toggleUseCase(uc: ModelUseCase) {
    setForm(f => {
      const has = f.useCases.includes(uc);
      const next = has ? f.useCases.filter(u => u !== uc) : [...f.useCases, uc];
      return { ...f, useCases: next.length > 0 ? next : f.useCases }; // keep at least one
    });
  }

  return (
    <div className={styles.page}>
      <PageGuide pageKey="llm-providers" />
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>LLM Providers</h1>
          <p className={styles.subtitle}>
            Add your own API keys to use Google Gemini, OpenAI, or Anthropic models.
            Keys are encrypted at rest and verified before saving.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button id="refresh-providers" className={styles.refreshBtn} onClick={load} title="Refresh">
            <RefreshCw size={14} />
          </button>
          <button id="add-provider" className={styles.addBtn} onClick={openCreate}>
            <Plus size={16} /> Add Provider
          </button>
        </div>
      </div>

      {/* ── Tenant configs ── */}
      <section className={styles.section}>

        {loading && <div className={styles.skeleton} />}

        {!loading && tenantConfigs.length === 0 && (
          <SettingsEmptyState
            icons={PROVIDERS.map(p => ({ icon: PROVIDER_META[p].icon, color: PROVIDER_META[p].color }))}
            title="Add your first LLM provider"
            description="Connect your own API key for Google Gemini, OpenAI, or Anthropic to power your agents with AI models."
            buttonLabel="Add your own key"
            onAction={openCreate}
          />
        )}

        {!loading && tenantConfigs.length > 0 && (
          <div className={styles.configGrid}>
            {tenantConfigs.map(cfg => (
              <ConfigCard
                key={cfg.id}
                config={cfg}
                onEdit={() => openEdit(cfg)}
                onToggle={() => handleToggle(cfg)}
                onDelete={() => setDeleteTarget(cfg)}
              />
            ))}
          </div>
        )}
      </section>


      {/* ── Add/Edit Modal ── */}
      {showModal && (
        <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>{editTarget ? 'Edit Provider' : 'Add LLM Provider'}</h2>
              <button className={styles.closeBtn} onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className={styles.modalBody}>
              {/* Provider selector */}
              <div className={styles.field}>
                <label className={styles.label}>Provider</label>
                <div className={styles.providerGrid}>
                  {PROVIDERS.map(p => {
                    const meta = PROVIDER_META[p];
                    const active = form.provider === p;
                    return (
                      <button
                        key={p}
                        id={`provider-${p}`}
                        type="button"
                        onClick={() => handleProviderChange(p)}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                          padding: '12px 8px', borderRadius: 10,
                          border: `1.5px solid ${active ? meta.color : 'var(--border)'}`,
                          background: active ? `${meta.color}12` : 'var(--surface-2)',
                          cursor: 'pointer', transition: 'all 0.15s',
                          color: active ? meta.color : 'var(--color-muted)',
                        }}
                      >
                        <span style={{ color: meta.color }}>{meta.icon}</span>
                        <span style={{ fontSize: 12, fontWeight: active ? 700 : 500 }}>{meta.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Use Cases (multi-select) */}
              <div className={styles.field}>
                <label className={styles.label}>Use Cases</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {USE_CASES.map(uc => {
                    const selected = form.useCases.includes(uc);
                    // In edit mode, lock to the single use case
                    const disabled = !!editTarget && uc !== form.useCases[0];
                    return (
                      <button
                        key={uc}
                        type="button"
                        onClick={() => !disabled && toggleUseCase(uc)}
                        style={{
                          padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                          border: `1.5px solid ${selected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                          background: selected ? 'var(--color-primary-dim)' : 'var(--color-surface-2)',
                          color: selected ? 'var(--color-primary)' : 'var(--color-text-3)',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          opacity: disabled ? 0.4 : 1,
                          transition: 'all 0.15s',
                        }}
                      >
                        {selected && <Check size={11} style={{ marginRight: 4 }} />}
                        {USE_CASE_LABELS[uc]}
                      </button>
                    );
                  })}
                </div>
                {!editTarget && <p className={styles.hint}>Select all that apply. One config is created per use case.</p>}
              </div>

              {/* Model */}
              <div className={styles.field}>
                <label className={styles.label}>Model</label>
                <select
                  id="model-name"
                  className={styles.select}
                  value={form.modelName}
                  onChange={e => { setForm(f => ({ ...f, modelName: e.target.value })); setVerifyResult(null); }}
                >
                  {provMeta.models.map(m => <option key={m.name} value={m.name}>{m.label}</option>)}
                </select>
              </div>

              {/* API Key with verify */}
              <div className={styles.field}>
                <label className={styles.label}>
                  API Key {editTarget && <span className={styles.optional}>(leave blank to keep existing)</span>}
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    id="api-key"
                    type="password"
                    className={styles.input}
                    value={form.apiKey}
                    onChange={e => { setForm(f => ({ ...f, apiKey: e.target.value })); setVerifyResult(null); }}
                    placeholder={editTarget ? '••••••••••••••••' : provMeta.keyPlaceholder}
                    autoComplete="new-password"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={handleVerify}
                    disabled={!form.apiKey.trim() || verifying}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px', borderRadius: 8,
                      background: verifyResult?.valid ? '#10b981' : `${provMeta.color}18`,
                      color: verifyResult?.valid ? '#fff' : provMeta.color,
                      fontWeight: 600, fontSize: 12, cursor: 'pointer',
                      opacity: !form.apiKey.trim() || verifying ? 0.5 : 1,
                      transition: 'all 0.2s', whiteSpace: 'nowrap',
                      border: `1px solid ${verifyResult?.valid ? '#10b981' : provMeta.color + '40'}`,
                    }}
                  >
                    {verifying ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Verifying…</> :
                     verifyResult?.valid ? <><ShieldCheck size={13} /> Verified</> :
                     <><Zap size={13} /> Verify Key</>}
                  </button>
                </div>
                {/* Verify result */}
                {verifyResult && !verifyResult.valid && (
                  <div style={{
                    marginTop: 8, padding: '8px 12px', borderRadius: 8,
                    background: '#ef444412', border: '1px solid #ef444430',
                    fontSize: 12, color: '#f87171', display: 'flex', alignItems: 'flex-start', gap: 6,
                  }}>
                    <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>{verifyResult.error}</span>
                  </div>
                )}
                {verifyResult?.valid && (
                  <div style={{
                    marginTop: 8, padding: '8px 12px', borderRadius: 8,
                    background: '#10b98112', border: '1px solid #10b98130',
                    fontSize: 12, color: '#10b981', display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <ShieldCheck size={13} />
                    Key is valid — {provMeta.name} responded in {verifyResult.latencyMs}ms
                  </div>
                )}
                <p className={styles.hint}>
                  Get your key from <a href={provMeta.docsUrl} target="_blank" rel="noopener" style={{ color: provMeta.color }}>{provMeta.name} Console</a>. Stored encrypted. Never logged.
                </p>
              </div>

              {/* Priority */}
              <div className={styles.field}>
                <label className={styles.label}>Priority</label>
                <p className={styles.hint} style={{ marginBottom: 4 }}>Lower = tried first. Multiple providers create a fallback chain.</p>
                <input
                  id="provider-priority"
                  type="number"
                  className={styles.input}
                  style={{ maxWidth: 100 }}
                  value={form.priority}
                  onChange={e => setForm(f => ({ ...f, priority: Number(e.target.value) }))}
                  min={1}
                  max={999}
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button
                id="save-provider"
                className={styles.saveBtn}
                onClick={handleSave}
                disabled={saving || !form.modelName.trim() || (!editTarget && !form.apiKey.trim()) || form.useCases.length === 0}
              >
                {saving ? 'Saving…' : <><Check size={14} /> {editTarget ? 'Update' : 'Add Provider'}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={deleteTarget ? `Remove ${PROVIDER_META[deleteTarget.provider].name} / ${deleteTarget.modelName}?` : ''}
        description="This may break conversations if no fallback provider exists for this use case."
        confirmLabel="Remove Provider"
        variant="danger"
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ── Config Card ───────────────────────────────────────────────────────────

function ConfigCard({
  config,
  onEdit,
  onToggle,
  onDelete,
}: {
  config: ModelProviderConfig;
  onEdit?: () => void;
  onToggle?: () => void;
  onDelete?: () => void;
}) {
  const meta = PROVIDER_META[config.provider];

  return (
    <div className={`${styles.configCard} ${!config.enabled ? styles.disabled : ''}`}>
      <div className={styles.cardTop}>
        <div className={styles.providerBadge} style={{ borderColor: meta.color + '44', background: meta.color + '11' }}>
          <span style={{ color: meta.color, display: 'flex', alignItems: 'center' }}>{meta.icon}</span>
          <span style={{ color: meta.color }}>{meta.name}</span>
        </div>
        <div className={styles.cardActions}>
          <button title="Edit" className={styles.iconBtn} onClick={onEdit}><Edit2 size={13} /></button>
          <button title={config.enabled ? 'Disable' : 'Enable'} className={styles.iconBtn} onClick={onToggle}>
            {config.enabled ? <ToggleRight size={16} style={{ color: 'var(--color-success)' }} /> : <ToggleLeft size={16} />}
          </button>
          <button title="Remove" className={`${styles.iconBtn} ${styles.danger}`} onClick={onDelete}><Trash2 size={13} /></button>
        </div>
      </div>

      <div className={styles.cardModel}>{config.modelName}</div>

      <div className={styles.cardMeta}>
        <span className={styles.metaChip}>{USE_CASE_LABELS[config.useCase]}</span>
        <span className={styles.metaChip}>Priority {config.priority}</span>
        <span className={styles.metaChip} style={config.encryptedKey ? { color: '#10b981', borderColor: '#10b98130', background: '#10b98112' } : {}}>
          {config.encryptedKey ? '🔑 Key set' : '🔓 No key'}
        </span>
      </div>

      {!config.enabled && <div className={styles.disabledBadge}>Disabled</div>}
    </div>
  );
}
